import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";
import {
  getAnalyticsOverview,
  getLeadPerformance,
  getMissedRecovery,
  getRevenueDaily,
  getRevenueSummary,
  getRouteLoad,
  getTechnicianCapacity,
} from "../../../api/analyticsApi";
import WidgetGrid from "../../ui/WidgetGrid";
import WidgetPanel from "../../ui/WidgetPanel";
import AnalyticsMetricCard from "../AnalyticsMetricCard";
// One null-aware definition for every Analytics page — see metricValue.js.
import { num, toMoney, safePct, ratioChange, sumKnown } from "../metricValue";
import KeyValueGrid from "../../ui/KeyValueGrid";
import { getIndustryKey, getTeamVocab } from "../../../constants/uiRegistry";
import OwnerScorecardCard from "../scorecard/OwnerScorecardCard";
import AnalyticsBrandFilter from "../scorecard/AnalyticsBrandFilter";
import "./AnalyticsDashboard.css";

// Per-industry framing for the analytics dashboard. Events has no technicians,
// routes, or drive time — showing them "Technician Capacity / Route Load" is the
// wrong-vertical bleed this gating removes. Pest is unchanged; landscaping keeps
// the dispatch cards but reads crew/visit. Unknown values stay "—", never 0.
const DASH_FRAMING = {
  events: {
    title: "Bookings & Revenue",
    subtitle: "Inquiries, bookings, revenue, and deposits for your events calendar.",
    conversionTitle: "Inquiry Conversion",
    conversionSub: "Booked / inquiries",
    leadSourcesTitle: "Top Inquiry Sources",
  },
  landscaping: {
    title: "Operations Dashboard",
    subtitle: "Crew utilization, visit load, and revenue across the schedule.",
    capacityTitle: "Crew Capacity",
    loadTitle: "Visit Load",
    productivityTitle: "Crew Productivity",
    heatmapTitle: "Visit Load Heatmap",
    conversionTitle: "Lead Conversion",
    conversionSub: "Converted / leads (FieldRoutes-linked)",
    leadSourcesTitle: "Top Lead Sources",
  },
  _default: {
    title: "Field Operations Dashboard",
    subtitle: null, // keep the existing recurring/one-time subtitle
    capacityTitle: "Technician Capacity",
    loadTitle: "Route Load",
    productivityTitle: "Technician Productivity",
    heatmapTitle: "Route Load Heatmap",
    conversionTitle: "Lead Conversion",
    conversionSub: "Converted / leads (FieldRoutes-linked)",
    leadSourcesTitle: "Top Lead Sources",
  },
};

const toRows = (payload) =>
  Array.isArray(payload) ? payload : Array.isArray(payload?.results) ? payload.results : [];

function RouteLoadHeatmap({ rows = [] }) {
  const grid = useMemo(() => {
    // Build a 2-week heatmap: rows are weekdays, columns are ISO dates.
    const byDate = new Map();
    rows.forEach((row) => {
      const date = row?.route_date;
      if (!date) return;
      const load = num(row?.load_rate);
      if (load === null) return;        // unknown: contributes nothing, scores nothing
      const prev = byDate.get(date) || [];
      prev.push(load);
      byDate.set(date, prev);
    });
    const dates = Array.from(byDate.keys()).sort();
    const last14 = dates.slice(-14);
    const weekdayLabel = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const buckets = weekdayLabel.map((label) => ({ label, cells: [] }));
    last14.forEach((iso) => {
      const d = new Date(`${iso}T00:00:00`);
      const weekday = Number.isNaN(d.getTime()) ? null : d.getDay();
      const values = byDate.get(iso) || [];
      const avg = values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0;
      buckets.forEach((b, idx) => {
        const isRow = weekday === idx;
        b.cells.push({
          key: `${b.label}-${iso}`,
          date: iso,
          value: isRow ? avg : null,
        });
      });
    });
    return { buckets, dates: last14 };
  }, [rows]);

  if (!grid?.dates?.length) {
    return <div className="ad-empty">No route load data yet.</div>;
  }

  const max = Math.max(
    0,
    ...grid.buckets.flatMap((b) => b.cells.map((c) => (c.value == null ? 0 : c.value)))
  );

  const colorFor = (v) => {
    if (v == null) return "transparent";
    const ratio = max > 0 ? Math.min(1, Math.max(0, v / max)) : 0;
    // Accessible warm ramp.
    const a = 0.08 + ratio * 0.32;
    return `rgba(249, 115, 22, ${a})`; // orange
  };

  return (
    <div className="ad-heatmap">
      <div className="ad-heatmap-head">
        <span>Route Load (Avg)</span>
        <span className="muted">Last {grid.dates.length} days</span>
      </div>
      <div className="ad-heatmap-grid" role="grid" aria-label="Route load heatmap">
        <div className="ad-heatmap-row head">
          <div className="ad-heatmap-y" />
          {grid.dates.map((d) => (
            <div key={d} className="ad-heatmap-x">
              {d.slice(5)}
            </div>
          ))}
        </div>
        {grid.buckets.map((bucket) => (
          <div key={bucket.label} className="ad-heatmap-row" role="row">
            <div className="ad-heatmap-y">{bucket.label}</div>
            {bucket.cells.map((cell) => (
              <div
                key={cell.key}
                className="ad-heatmap-cell"
                title={cell.value == null ? "" : `${bucket.label} ${cell.date}: ${(cell.value * 100).toFixed(0)}%`}
                style={{ background: colorFor(cell.value) }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [revenueSummary, setRevenueSummary] = useState(null);
  const [overview, setOverview] = useState(null);
  const [revenueDaily, setRevenueDaily] = useState([]);
  const [techCapacity, setTechCapacity] = useState(null);
  const [routeLoad, setRouteLoad] = useState(null);
  const [missedRecovery, setMissedRecovery] = useState(null);
  const [leadPerf, setLeadPerf] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setErrors({});
      try {
        const settled = await Promise.allSettled([
          getRevenueSummary(),
          getAnalyticsOverview(),
          getRevenueDaily({ days: 30 }),
          getTechnicianCapacity(),
          getRouteLoad({ days: 14 }),
          getMissedRecovery(),
          getLeadPerformance({ days: 180 }),
        ]);
        if (!mounted) return;

        const nextErrors = {};
        const pick = (idx) => {
          const item = settled[idx];
          if (item.status === "fulfilled") return item.value?.data;
          nextErrors[idx] = item.reason?.message || "Request failed";
          return null;
        };

        setRevenueSummary(pick(0));
        setOverview(pick(1));
        setRevenueDaily(toRows(pick(2)));
        setTechCapacity(pick(3));
        setRouteLoad(pick(4));
        setMissedRecovery(pick(5));
        setLeadPerf(pick(6));
        setErrors(nextErrors);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const isRecurring = String(revenueSummary?.model || "") === "recurring";

  const revenueTrend = useMemo(() => {
    const rows = Array.isArray(revenueDaily) ? revenueDaily : [];
    if (!rows.length) return { chart: [], current7: null, prev7: null, missingDays: 0, partial: false };
    // null (not 0) leaves a GAP in the line. Recharts does not connect nulls by
    // default, so an unreported day reads as "no data" instead of a crash to $0.
    const chart = rows.map((r) => ({
      label: String(r.date || "").slice(5),
      revenue: num(r.total_revenue),
    }));
    const last14 = rows.slice(-14);
    // Sum only the days that reported. Coercing a missing day to 0 dragged the
    // comparison down and rendered as a real decline — a fake number driving a
    // judgement. A window with any missing day is `partial`, and says so.
    const read = (r) => r.total_revenue;
    const prevStats = sumKnown(last14.slice(0, 7), read);
    const currStats = sumKnown(last14.slice(7), read);
    const missingDays = prevStats.missing + currStats.missing;
    return {
      chart,
      prev7: prevStats.total,
      current7: currStats.total,
      missingDays,
      partial: prevStats.partial || currStats.partial,
    };
  }, [revenueDaily]);

  const routeLoadTrend = useMemo(() => {
    const rows = Array.isArray(routeLoad?.rows) ? routeLoad.rows : [];
    if (!rows.length) return { avg: null, prev: null, trend: null };
    const byDate = new Map();
    rows.forEach((r) => {
      const d = r.route_date;
      if (!d) return;
      const v = num(r.load_rate);
      if (v === null) return;           // unknown: not an idle route
      const arr = byDate.get(d) || [];
      arr.push(v);
      byDate.set(d, arr);
    });
    const dates = Array.from(byDate.keys()).sort();
    const last14 = dates.slice(-14);
    const avgFor = (dateList) => {
      const values = dateList.flatMap((d) => byDate.get(d) || []);
      return values.length ? values.reduce((s, v) => s + v, 0) / values.length : null;
    };
    const prev = avgFor(last14.slice(0, 7));
    const avg = avgFor(last14.slice(7));
    return { avg, prev, trend: ratioChange(avg, prev) };
  }, [routeLoad]);

  const techTrend = useMemo(() => {
    return { avg: num(techCapacity?.technician_utilization_avg), trend: null };
  }, [techCapacity]);

  const missedTrend = useMemo(() => {
    // 0 missed appointments is a real, good answer — distinct from "we don't know".
    return { missed: num(missedRecovery?.missed_appointments), trend: null };
  }, [missedRecovery]);

  const leadTrend = useMemo(() => {
    const rate = Number(leadPerf?.lead_conversion_rate ?? 0);
    return { rate, trend: null };
  }, [leadPerf]);

  const industryKey = getIndustryKey();
  const isEvents = industryKey === "events";
  const framing = DASH_FRAMING[industryKey] || DASH_FRAMING._default;

  // Events metrics come from the overview funnel (counts), not routes/techs.
  const eventsBookings = num(overview?.funnel_30d?.booked);
  const eventsInquiries = num(overview?.funnel_30d?.leads ?? overview?.leads_active);

  const topLeadSources = useMemo(() => {
    const rows = Array.isArray(leadPerf?.revenue_per_lead_source) ? leadPerf.revenue_per_lead_source : [];
    return rows.slice(0, 6).map((r) => ({
      source: r.lead_source,
      revenue: num(r.revenue),
      converted: num(r.converted),
      leads: num(r.leads),
    }));
  }, [leadPerf]);

  return (
    <div className="analytics-dashboard">
      <header className="ad-head">
        <div>
          <p className="eyebrow">Analytics</p>
          <h1>{framing.title}</h1>
          <span>
            {framing.subtitle
              ? framing.subtitle
              : isRecurring
              ? "Recurring tenants read revenue from service agreements, active MRR, and agreement health."
              : "One-time tenants read revenue from normalized quotes, collections, and close performance."}
          </span>
        </div>
        <AnalyticsBrandFilter />
      </header>

      <OwnerScorecardCard />

      {Object.keys(errors).length ? (
        <div className="ad-warn">
          Some analytics endpoints are unavailable right now (expected during FieldRoutes backfill). The dashboard is showing partial data.
        </div>
      ) : null}

      <WidgetGrid columns={3} className="ad-metric-grid">
        <AnalyticsMetricCard
          title={isRecurring ? "Active MRR" : "Booked Revenue"}
          subtitle={isRecurring ? "Active agreements only" : "Normalized quote value excluding drafts"}
          value={toMoney(isRecurring ? revenueSummary?.mrr : revenueSummary?.booked_revenue)}
          trend={null}
          periodLabel={isRecurring ? "current monthly run-rate" : "current booked pipeline"}
          loading={loading}
          onClick={() => navigate("/analytics/revenue")}
        />
        <AnalyticsMetricCard
          title={framing.conversionTitle}
          subtitle={framing.conversionSub}
          value={safePct(leadTrend.rate)}
          trend={leadTrend.trend}
          periodLabel="vs prior period"
          loading={loading}
          onClick={() => navigate("/analytics/lead-conversion")}
        />
        <AnalyticsMetricCard
          title={isRecurring ? "Remaining Revenue" : "Collected Revenue"}
          subtitle={isRecurring ? "Contract value still uncollected" : "Recognized cash from quotes"}
          value={toMoney(isRecurring ? revenueSummary?.remaining_revenue : revenueSummary?.collected_revenue)}
          trend={null}
          periodLabel={isRecurring ? "agreements + orphan quotes" : "recognized revenue"}
          loading={loading}
          tone="good"
          onClick={() => navigate("/analytics/revenue")}
        />
        {isEvents ? (
          <AnalyticsMetricCard
            title="Bookings"
            subtitle="Confirmed bookings, last 30 days"
            value={eventsBookings === null ? "—" : String(eventsBookings)}
            trend={null}
            periodLabel="last 30 days"
            loading={loading}
            onClick={() => navigate("/schedule")}
          />
        ) : (
          <AnalyticsMetricCard
            title={framing.capacityTitle}
            subtitle="Average utilization today"
            value={techTrend.avg === null ? "—" : `${Math.round(techTrend.avg)}%`}
            trend={techTrend.trend}
            periodLabel="today"
            loading={loading}
            onClick={() => navigate("/analytics/technicians")}
          />
        )}
        {isEvents ? (
          <AnalyticsMetricCard
            title="Inquiries"
            subtitle="New inquiries, last 30 days"
            value={eventsInquiries === null ? "—" : String(eventsInquiries)}
            trend={null}
            periodLabel="last 30 days"
            loading={loading}
            onClick={() => navigate("/leads")}
          />
        ) : (
          <AnalyticsMetricCard
            title={framing.loadTitle}
            subtitle="Load rate (scheduled / capacity)"
            value={safePct(routeLoadTrend.avg)}
            trend={routeLoadTrend.trend}
            periodLabel="vs prior 7 days"
            loading={loading}
            onClick={() => navigate("/analytics/routes")}
          />
        )}
        {isEvents ? (
          <AnalyticsMetricCard
            title="Space Utilization"
            subtitle="Venue booked vs available"
            value="—"
            trend={null}
            periodLabel="not yet measured"
            loading={loading}
          />
        ) : (
          <AnalyticsMetricCard
            title="Missed Recovery"
            subtitle="Missed/cancelled opportunities"
            value={missedTrend.missed === null ? "—" : String(missedTrend.missed)}
            trend={missedTrend.trend}
            periodLabel="open recovery queue"
            loading={loading}
            tone={missedTrend.missed ? "bad" : "neutral"}   /* unknown is not "bad" */
            onClick={() => navigate("/analytics/recovery")}
          />
        )}
        {isEvents ? (
          <AnalyticsMetricCard
            title="Deposits Outstanding"
            subtitle="Deposits invoiced, not yet paid"
            value="—"
            trend={null}
            periodLabel="not yet measured"
            loading={loading}
            onClick={() => navigate("/finance")}
          />
        ) : (
          <AnalyticsMetricCard
            title="Customer Lifetime Value"
            subtitle={isRecurring ? "Churned and paused recurring revenue" : "One-time close performance"}
            value={isRecurring ? toMoney(revenueSummary?.churned_mrr) : safePct(revenueSummary?.conversion_rate)}
            trend={null}
            periodLabel={isRecurring ? `paused ${toMoney(revenueSummary?.paused_mrr)}` : "quote completion"}
            loading={loading}
            onClick={() => navigate("/analytics/revenue")}
          />
        )}
      </WidgetGrid>

      <div className="ad-grid">
        <WidgetPanel
          title="Revenue Trend (Daily)"
          subtitle={
            revenueTrend.partial
              ? `Partial — ${revenueTrend.missingDays} ${revenueTrend.missingDays === 1 ? "day has" : "days have"} no data and are skipped, not counted as zero`
              : "Total revenue from rollups"
          }
        >
          {revenueTrend.chart.length ? (
            <div className="ad-chart">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={revenueTrend.chart}>
                  <CartesianGrid stroke="rgba(148,163,184,0.18)" strokeDasharray="3 3" />
                  <XAxis dataKey="label" stroke="var(--text-sub)" />
                  <YAxis stroke="var(--text-sub)" />
                  <Tooltip
                    contentStyle={{
                      background: "var(--surface-secondary)",
                      border: "1px solid var(--panel-border)",
                      borderRadius: 10,
                      color: "var(--text-main)",
                    }}
                  />
                  <Line type="monotone" dataKey="revenue" stroke="var(--accent)" strokeWidth={3} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="ad-empty">No revenue rollups yet (RevenueRecord can be 0 during import).</div>
          )}
        </WidgetPanel>

        {!isEvents && (<>
        <WidgetPanel title={framing.productivityTitle} subtitle="Appointments scheduled today">
          {Array.isArray(techCapacity?.technicians) && techCapacity.technicians.length ? (
            <div className="ad-chart">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={techCapacity.technicians.slice(0, 10).map((t) => ({
                    name: t.technician_name,
                    // A tech whose numbers did not report is UNKNOWN, not idle.
                    // Coercing to 0 drew a zero-height bar against a named
                    // person and read as "did no work today".
                    appts: num(t.appointments_today),
                    util: num(t.utilization_percent),
                  }))}
                >
                  <CartesianGrid stroke="rgba(148,163,184,0.18)" strokeDasharray="3 3" />
                  <XAxis dataKey="name" stroke="var(--text-sub)" hide />
                  <YAxis stroke="var(--text-sub)" />
                  <Tooltip
                    contentStyle={{
                      background: "var(--surface-secondary)",
                      border: "1px solid var(--panel-border)",
                      borderRadius: 10,
                      color: "var(--text-main)",
                    }}
                  />
                  <Bar dataKey="appts" fill="var(--accent)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="ad-empty">No technician capacity data yet.</div>
          )}
        </WidgetPanel>

        <WidgetPanel title={framing.heatmapTitle} subtitle="Capacity intensity by day">
          <RouteLoadHeatmap rows={Array.isArray(routeLoad?.rows) ? routeLoad.rows : []} />
        </WidgetPanel>
        </>)}

        <WidgetPanel title={framing.leadSourcesTitle} subtitle="Revenue by source (when available)">
          {topLeadSources.length ? (
            <div className="ad-table">
              <div className="ad-table-head">
                <span>Source</span>
                <span>Leads</span>
                <span>Converted</span>
                <span>Revenue</span>
              </div>
              {topLeadSources.map((row) => (
                <div key={row.source} className="ad-table-row">
                  <strong title={row.source}>{row.source}</strong>
                  <span>{row.leads}</span>
                  <span>{row.converted}</span>
                  <span>{toMoney(row.revenue)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="ad-empty">Lead performance data not available yet.</div>
          )}
        </WidgetPanel>
      </div>

      {/* Debug surface: keeps all backend fields visible without bloating primary UI */}
      <div className="ad-debug">
        <KeyValueGrid title="Overview Payload" data={overview || {}} />
      </div>
    </div>
  );
}
